import React from 'react';
import {ScrollView, View} from 'react-native';
import ReactNativeModal from 'react-native-modal';
import {Icon, makeStyles} from '@rneui/themed';

import config from '../../../config';
import useIsLoggedIn from '../../../hooks/useIsLoggedIn';
import {AppRoute} from '../../../models/AppRoute';
import {useStores} from '../../../stores/use-stores';
import {rewardsMarketingName} from '../../../utils/rewards';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';
import RewardsProgramImage from '../ProgramImage';

type JoinMoreRewardsContentProps = {
  onLogMeInThenActivate?: () => void;
  onCreateAccountThenActivate?: () => void;
  onActivateRewardsForExisting?: () => void;
  onClose: () => void;
};

function JoinMoreRewardsContent({
  onClose,
  onCreateAccountThenActivate: onCreateAccount,
  onLogMeInThenActivate: onLogMeIn,
}: JoinMoreRewardsContentProps) {
  const styles = useStyles();
  return (
    <View style={{backgroundColor: 'white'}}>
      <PressableWithOpacityFeedback
        style={{alignSelf: 'flex-end', marginTop: 12, marginRight: 12}}
        onPress={onClose}>
        <Icon
          name="close"
          type="material"
          size={36}
          color={styles.icon.color}
        />
      </PressableWithOpacityFeedback>
      <RewardsProgramImage variant="onLight" />

      <View style={{paddingHorizontal: 20, paddingBottom: 20}}>
        <Text style={{textAlign: 'center'}} variant="heading1">
          Join {rewardsMarketingName}!
        </Text>
        <Text style={{textAlign: 'center'}}>
          Join our rewards program to access member only pricing and exclusive
          deals!
        </Text>
        <View style={{marginVertical: 16}}>
          <Text variant="heading3">New here?</Text>
          <Text>Create an account to join {rewardsMarketingName} today</Text>
          <Button buttonStyle={{marginVertical: 8}} onPress={onCreateAccount}>
            CREATE ACCOUNT
          </Button>
        </View>

        <View>
          <Text variant="heading3">
            Have a {config.APP_NAME} account already?
          </Text>
          <Text>Activate your rewards when you log in </Text>
          <Button
            buttonStyle={{marginVertical: 8}}
            onPress={onLogMeIn}
            variant="outlined">
            LOG ME IN
          </Button>
        </View>
      </View>
    </View>
  );
}

type JoinMoreRewardsCTAProps = Omit<
  JoinMoreRewardsContentProps,
  | 'onClose'
  | 'onActivateRewardsForExisting'
  | 'onLogMeInThenActivate'
  | 'onCreateAccountThenActivate'
> & {
  navigation: any;
};

function JoinMoreRewardsCTA(props: JoinMoreRewardsCTAProps) {
  const [show, setShow] = React.useState(false);
  const onClose = () => setShow(false);
  const {userStore} = useStores();

  const loggedIn = useIsLoggedIn();

  /**
   * This is the start screen of rewards activate flow
   */
  const START_REWARDS_ACTIVATE_SCREEN =
    AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS;

  /**
   * When user is not logged in and will create a mfl account
   */
  const handleCreateAccountThenActivate = () => {
    props.navigation.navigate(
      loggedIn ? AppRoute.ACCOUNT_LOGGED_IN : AppRoute.ACCOUNT_NOT_LOGGED_IN,
    );
  };

  /**
   * When user is not logged in but already has mfl account
   * redirect to log in and then activate rewards flow
   */
  const handleLogMeInThenActivate = () => {
    userStore.setPostLoginReturnToScreen(AppRoute.ROOT_REWARDS_FLOW, {
      screen: START_REWARDS_ACTIVATE_SCREEN,
      params: {activateFlow: 'logMeIn'},
    });

    props.navigation.navigate(
      loggedIn ? AppRoute.ACCOUNT_LOGGED_IN : AppRoute.ACCOUNT_NOT_LOGGED_IN,
    );
  };

  /**
   * When user is logged in this will kick off the activate rewards flow
   */
  const handleActivateRewardsForExisting = () => {
    /**
     * kick off the activate rewards flow
     * navigating to a different navigation stack is untyped so we need to use any
     */
    props.navigation.navigate(
      AppRoute.ROOT_REWARDS_FLOW as any,
      {
        screen: START_REWARDS_ACTIVATE_SCREEN,
      } as any,
    );
  };

  return (
    <>
      <Button
        onPress={() => {
          if (!loggedIn) {
            setShow(true);
          } else {
            handleActivateRewardsForExisting();
          }
        }}>
        JOIN NOW
      </Button>
      <ReactNativeModal
        isVisible={show}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        // style={{padding: 0, margin: 0}}
      >
        <JoinMoreRewardsContent
          {...props}
          onClose={onClose}
          onLogMeInThenActivate={() => {
            setShow(false);
            handleLogMeInThenActivate();
          }}
          onCreateAccountThenActivate={() => {
            setShow(false);
            handleCreateAccountThenActivate();
          }}
        />
      </ReactNativeModal>
    </>
  );
}

export {JoinMoreRewardsCTA};

const useStyles = makeStyles(theme => ({
  icon: {color: theme.colors?.legacy?.text?.heading?.primary},
}));
